<template>
  <v-container class="mw-640" :class="{ 'pa-0': isModal }">
    <v-card class="rounded-xxl" outlined>
      <!-- Header -->
      <CardToolbarDialog title="New Category" @cancel="cancel" />

      <v-card-text class="pa-5">
        <!-- Image -->
        <div class="d-flex justify-center mb-4">
          <ImageBox
            class="category-image"
            :imageURL="category.imageURL"
            @setImage="setImage"
          />
        </div>

        <!-- Name -->
        <v-text-field
          v-model.trim="category.name"
          label="Category Name"
          outlined
          dense
          autofocus
          :error-messages="nameErrors"
          :hide-details="nameErrors.length === 0"
          @input="onNameInput"
          @blur="$v.category.name.$touch()"
        />

        <!-- Master Category -->
        <v-select
          v-model="category.MasterCategoryId"
          :items="masterCategories"
          item-text="name"
          item-value="id"
          label="Master Category"
          outlined
          dense
          :error-messages="masterCategoryErrors"
          :hide-details="masterCategoryErrors.length === 0"
          @change="$v.category.MasterCategoryId.$touch()"
          @blur="$v.category.MasterCategoryId.$touch()"
        >
          <!-- nicer dropdown -->
          <template v-slot:item="{ item }">
            <div class="py-2">
              <div class="font-weight-medium">{{ item.name }}</div>
              <div v-if="item.description" class="caption grey--text text--darken-1">
                {{ item.description }}
              </div>
            </div>
          </template>
        </v-select>
      </v-card-text>

      <v-divider />

      <!-- Actions -->
      <v-card-actions class="px-4 py-3">
        <v-btn text class="text-capitalize" @click="cancel">Cancel</v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          class="text-capitalize"
          :loading="saving"
          :disabled="saving"
          @click="submit"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import publicService from "@/api/public.js";
import ImageBox from "@/components/ImageBox";
import categoryService from "../service";

const { validationMixin } = require("vuelidate");
const { required, minLength, minValue } = require("vuelidate/lib/validators");

export default {
  name: "category-create-v2",
  props: ["isModal", "searchText"],
  components: { ImageBox },
  mixins: [validationMixin],

  data() {
    return {
      saving: false,
      masterCategories: [],
      category: {
        name: "",
        imageURL: null,
        MasterCategoryId: null,
      },
      savedCategory: null,

      // prevents searchText watcher from fighting user input
      initializedFromSearch: false,
      userEditedName: false,
    };
  },

  validations: {
    category: {
      name: { required, minLength: minLength(4) },
      // IMPORTANT: required + minValue so null fails too
      MasterCategoryId: { required, minValue: minValue(1) },
    },
  },

  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.category.name.$dirty) return errors;
      if (!this.$v.category.name.required) errors.push("Name is required");
      if (!this.$v.category.name.minLength)
        errors.push("Name must be at least 4 characters");
      return errors;
    },

    masterCategoryErrors() {
      const errors = [];
      if (!this.$v.category.MasterCategoryId.$dirty) return errors;
      if (!this.$v.category.MasterCategoryId.required)
        errors.push("Master Category is required");
      if (!this.$v.category.MasterCategoryId.minValue)
        errors.push("Master Category is required");
      return errors;
    },
  },

  async created() {
    try {
      const response = await publicService.MasterCategories();
      this.masterCategories = (response.data || []).map((c) => ({
        ...c,
        id: Number(c.id),
      }));
    } catch (error) {
      console.log(error);
    }
  },

  watch: {
    searchText: {
      immediate: true,
      handler(val) {
        const v = (val || "").trim();
        if (!v) return;

        if (
          (!this.initializedFromSearch || !this.category.name) &&
          !this.userEditedName
        ) {
          this.category.name = v;
          this.initializedFromSearch = true;
          this.$nextTick(() => this.$v.category.name.$touch());
        }
      },
    },
  },

  methods: {
    setImage(val) {
      this.category.imageURL = val;
    },

    onNameInput() {
      this.userEditedName = true;
      this.$v.category.name.$touch();
    },

    async submit() {
      this.$v.$touch();

      // quick guard (UX)
      if (!this.category.MasterCategoryId) {
        this.$swal({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Master Category is required",
          showConfirmButton: false,
          timer: 2000,
        });
        return;
      }

      if (this.$v.$invalid) {
        this.$swal({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Please fix the highlighted fields",
          showConfirmButton: false,
          timer: 2000,
        });
        return;
      }

      this.saving = true;
      this.$Progress.start();

      try {
        const response = await categoryService.create(this.category);
        this.savedCategory = response.data;

        this.$swal({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Category was created",
          showConfirmButton: false,
          timer: 2000,
        });

        this.$Progress.finish();
        this.close();
      } catch (error) {
        console.log(error);
        this.$Progress.fail();

        this.$swal({
          toast: true,
          position: "top-end",
          icon: "error",
          title: error?.data?.message || "Category was not created",
          showConfirmButton: false,
          timer: 3000,
        });
      } finally {
        this.saving = false;
      }
    },

    cancel() {
      this.close();
    },

    close() {
      if (!this.isModal) {
        this.$router.go(-1);
      } else {
        this.$emit("close", this.savedCategory);
      }
    },
  },
};
</script>

<style scoped>
.mw-640 {
  max-width: 640px;
  margin: auto;
}
.category-image {
  width: 180px;
}
</style>